/**
 * RowHeaderSlot - Renders controls in the row-header location.
 *
 * This slot appears before each row (full width) and is typically used
 * to show row labels in grid mode (e.g., run names, step values).
 */

import React from 'react';

import {TileTitle} from '../../controls';
import {ControlDefinition, GridRow, isTileDimension} from '../types';

export type RowHeaderSlotProps = {
  controls: ControlDefinition[];
  row: GridRow;
  disableRunLinks?: boolean;
};

/**
 * Renders controls in the row-header location.
 *
 * Automatically determines which dimension to display based on slot configuration.
 * Uses the first cell in the row for dimension data.
 *
 * @param props - Row header slot props
 * @returns Rendered row header or null if hidden/empty
 */
export const RowHeaderSlot: React.FC<RowHeaderSlotProps> = ({
  controls,
  row,
  disableRunLinks,
}) => {
  // Find the dimension control (run/step/index) that should be displayed as a label in this row header.
  // We filter for 'label' type controls - these are dimensions shown as static text labels
  // rather than as interactive controls (sliders/tabs/dropdowns).
  const labelControl = controls.find(
    c => c.type === 'label' && isTileDimension(c.control)
  );

  // Don't render if no dimension found or row is empty
  if (
    !labelControl ||
    !isTileDimension(labelControl.control) ||
    row.cells.length === 0 ||
    row.cells[0].isEmpty ||
    labelControl.control === 'index'
  ) {
    return null;
  }

  return (
    <div className="panel-media__grid-row" style={{textAlign: 'center'}}>
      <TileTitle
        dimension={labelControl.control}
        cell={row.cells[0]}
        disableRunLinks={disableRunLinks}
      />
    </div>
  );
};
